import React, { useMemo } from 'react'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import { Badge } from 'reactstrap'
import {
  CC_ESTEIRA_STEPS,
  formatBudgetMoney,
} from './budgetDashboardHelpers'
import { DRILL_TYPES } from './budgetDrilldownHelpers'

const mrtProps = {
  muiTableBodyCellProps: { sx: { fontSize: '11px' } },
  muiTableHeadCellProps: {
    sx: {
      '.MuiSvgIcon-root': { fontSize: '18px' },
      fontSize: '12px',
      fontWeight: 'normal',
      padding: '8px',
    },
  },
  muiTablePaginationProps: {
    sx: {
      '.MuiTablePagination-displayedRows': { fontSize: '12px' },
      marginTop: '0px',
      fontSize: '12px',
    },
    labelRowsPerPage: <span style={{ fontSize: '12px' }}>Linhas por página</span>,
    rowsPerPageOptions: [10, 25, 50, 100],
  },
  enableDensityToggle: false,
  enableColumnFilters: false,
  enableSorting: true,
  enableMultiSort: true,
  positionPagination: 'bottom',
}

const TYPE_LABELS = {
  [DRILL_TYPES.CC]: 'Centro de custo',
  [DRILL_TYPES.RUBRICA]: 'Rubrica',
  [DRILL_TYPES.SUBRUBRICA]: 'Subrubrica',
  [DRILL_TYPES.ITEM]: 'Item',
}

const NAME_HEADERS = {
  [DRILL_TYPES.CC]: 'Centro de custo',
  [DRILL_TYPES.RUBRICA]: 'Rubrica',
  [DRILL_TYPES.SUBRUBRICA]: 'Subrubrica',
  [DRILL_TYPES.ITEM]: 'Item orçamentário',
}

function moneyCell({ cell }) {
  const v = Number(cell.getValue() || 0)
  if (v === 0) return <span className="text-muted">—</span>
  return formatBudgetMoney(v)
}

function moneySortingFn(rowA, rowB, columnId) {
  return Number(rowA.getValue(columnId) || 0) - Number(rowB.getValue(columnId) || 0)
}

function fmtPct(v) {
  if (v == null || v === '') return '—'
  return `${v}%`
}

function pctOrcadoSortingFn(rowA, rowB, columnId) {
  const a = Number(rowA.getValue(columnId) ?? -1)
  const b = Number(rowB.getValue(columnId) ?? -1)
  return a - b
}

function PctOrcadoCell({ row, pctKey, valorKey }) {
  const orcado = Number(row.original.orcado || 0)
  const pct = row.original[pctKey]
  if (!(orcado > 0)) {
    return Number(row.original[valorKey] || 0) > 0 ? '—' : '0%'
  }
  const acimaDoTeto = pct != null && pct > 100
  return (
    <span style={acimaDoTeto ? { color: '#f46a6a', fontWeight: 600 } : undefined}>
      {fmtPct(pct)}
    </span>
  )
}

const PCT_ORCADO_COLUMNS = [
  { accessorKey: 'pctProgramado', header: 'Prog. ÷ Orçado', valorKey: 'programado' },
  { accessorKey: 'pctComprometido', header: 'Comp. ÷ Orçado', valorKey: 'comprometido' },
  { accessorKey: 'pctLiberado', header: 'Liber. ÷ Orçado', valorKey: 'liberado' },
  { accessorKey: 'pctPago', header: 'Pago ÷ Orçado', valorKey: 'pago' },
]

const BudgetVisaoGeralTable = ({
  cards,
  drillLevel,
  drillPath,
  selectedOrcItemId,
  onRowClick,
}) => {
  const columns = useMemo(() => {
    const cols = [
      {
        accessorKey: 'cardType',
        header: 'Tipo',
        size: 110,
        accessorFn: row => TYPE_LABELS[row.cardType] || row.cardType,
        sortingFn: 'alphanumeric',
      },
      {
        accessorKey: 'cardNome',
        header: NAME_HEADERS[drillLevel] || 'Nome',
        size: 220,
        sortingFn: 'alphanumeric',
      },
      {
        accessorKey: 'qtdItens',
        header: 'Itens',
        size: 72,
        sortingFn: 'basic',
        Cell: ({ cell }) => {
          const v = Number(cell.getValue() || 0)
          return v > 0 ? v : '—'
        },
      },
      {
        accessorKey: 'qtdContratos',
        header: 'Contratos',
        size: 88,
        sortingFn: 'basic',
        Cell: ({ cell }) => {
          const v = Number(cell.getValue() || 0)
          return v > 0 ? v : '—'
        },
      },
    ]

    for (const step of CC_ESTEIRA_STEPS) {
      cols.push({
        accessorKey: step.key,
        header: step.label,
        size: 118,
        sortingFn: moneySortingFn,
        Cell: moneyCell,
      })
    }

    cols.push(
      {
        accessorKey: 'pctDistribuido',
        header: 'Distrib. ÷ Orçado',
        size: 110,
        sortingFn: 'basic',
        Cell: ({ row }) => {
          const orcado = Number(row.original.orcado || 0)
          const pct = row.original.pctDistribuido ?? 0
          if (!(orcado > 0)) {
            return Number(row.original.distribuido || 0) > 0 ? '—' : '0%'
          }
          return (
            <span style={row.original.estouro ? { color: '#f46a6a', fontWeight: 600 } : undefined}>
              {pct}
              %
            </span>
          )
        },
      },
      ...PCT_ORCADO_COLUMNS.map(col => ({
        accessorKey: col.accessorKey,
        header: col.header,
        size: 100,
        sortingFn: pctOrcadoSortingFn,
        Cell: ({ row }) => (
          <PctOrcadoCell row={row} pctKey={col.accessorKey} valorKey={col.valorKey} />
        ),
      })),
      {
        accessorKey: 'disponivel',
        header: 'Disponível',
        size: 118,
        sortingFn: moneySortingFn,
        Cell: moneyCell,
      },
      {
        accessorKey: 'pctDisponivel',
        header: 'Disp. ÷ Orçado',
        size: 100,
        sortingFn: pctOrcadoSortingFn,
        Cell: ({ row }) => (
          <PctOrcadoCell row={row} pctKey="pctDisponivel" valorKey="disponivel" />
        ),
      },
      {
        accessorKey: 'disponivelDistribuir',
        header: 'Saldo p/ distribuir',
        size: 130,
        sortingFn: moneySortingFn,
        Cell: moneyCell,
      },
      {
        id: 'indicadores',
        header: 'Indicadores',
        size: 90,
        enableSorting: false,
        Cell: ({ row }) => {
          const card = row.original
          if (!card.estouro) return '—'
          return (
            <Badge color="danger" pill style={{ fontSize: '0.65rem' }}>Estouro</Badge>
          )
        },
      },
    )

    return cols
  }, [drillLevel])

  const isRowSelected = (card) => {
    if (card.cardType === DRILL_TYPES.ITEM) {
      return String(selectedOrcItemId) === String(card.orcItemId)
    }
    return drillPath.some(n => String(n.id) === String(
      card.cardType === DRILL_TYPES.CC ? card.ccId
        : card.cardType === DRILL_TYPES.RUBRICA ? card.rubricaId
          : card.subrubricaId,
    ))
  }

  if (!cards?.length) {
    return null
  }

  return (
    <MaterialReactTable
      columns={columns}
      data={cards}
      getRowId={row => String(row.cardId)}
      localization={MRT_Localization_PT}
      initialState={{
        density: 'compact',
        pagination: { pageIndex: 0, pageSize: 25 },
        sorting: [{ id: 'cardNome', desc: false }],
      }}
      enableColumnResizing
      enableGlobalFilter={false}
      enableStickyHeader
      enablePagination={cards.length > 10}
      {...mrtProps}
      muiTablePaperProps={{ sx: { boxShadow: 'none', border: '1px solid #eef0f3' } }}
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => onRowClick?.(row.original),
        sx: {
          cursor: 'pointer',
          backgroundColor: isRowSelected(row.original) ? 'rgba(86, 100, 210, 0.08)' : undefined,
        },
      })}
    />
  )
}

export default BudgetVisaoGeralTable
